import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  CellValueChangedEvent,
  GetRowIdParams,
  IRowNode,
} from 'ag-grid-community'
import { cloneDeep, isEqual } from 'lodash'
import { AGGrid } from '@/components/atoms/AGGrid'
import { isRecord } from '@/utils/typeGuards'
import type { GridCellEditOperation, GridContextRefreshPlan } from '../types'

import { useGridApi, useGridTable } from './GridRoot'
import {
  refreshAggregationAfterCellEdit,
  shouldRefreshAggregationAfterRowDataDiff,
} from './aggregationRefresh'
import {
  createLocalRowTransition,
  type LocalRowTransition,
  recordLocalRowOperation,
  rememberControlledRow,
  settleLocalRowOperation,
  shouldDeferLocalRow,
} from './localRowTransition'
import { clearRemovedVirtualizedCellFocus } from '../utils/focusBeforeRowRemoval'

type ContextRefreshSignalMap = Record<
  string,
  string | number | boolean | undefined
>

type GridDebugCounters = {
  refreshCells: number
  redrawRows: number
  refreshClientSideRowModel: number
  undoEnded: number
  redoEnded: number
}

type GridDebugStore = {
  counters: GridDebugCounters
  reset: () => void
}

type ResolvedContextVisualRefresh =
  | { kind: 'none' }
  | { kind: 'broad' }
  | { kind: 'plan'; plan: GridContextRefreshPlan }

type PendingVisualContextRefresh = {
  contextVisualRefresh: ResolvedContextVisualRefresh
  rowClassChanged: boolean
  quickFilterInvalidated: boolean
}

const isPersistencePromise = (value: unknown): value is Promise<unknown> =>
  value !== null &&
  (typeof value === 'object' || typeof value === 'function') &&
  'then' in value &&
  typeof value.then === 'function'

const isCellEditOperation = (value: unknown): value is GridCellEditOperation =>
  isRecord(value) &&
  Array.isArray(value.rowIds) &&
  value.rowIds.every((id) => typeof id === 'string') &&
  isPersistencePromise(value.settled)

const coerceRowId = (value: unknown): string | null => {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return null
}

const shouldUpdateRow = <Row,>(previous: Row, next: Row): boolean => {
  if (previous === next) return false
  if (!isRecord(previous) || !isRecord(next)) return true

  const prevUpdatedAt = previous.updatedAt
  const nextUpdatedAt = next.updatedAt
  if (typeof prevUpdatedAt === 'string' && typeof nextUpdatedAt === 'string') {
    if (prevUpdatedAt !== nextUpdatedAt) return true
  }

  const prevVersion = previous.version
  const nextVersion = next.version
  if (typeof prevVersion === 'string' && typeof nextVersion === 'string') {
    if (prevVersion !== nextVersion) return true
  }
  if (typeof prevVersion === 'number' && typeof nextVersion === 'number') {
    if (prevVersion !== nextVersion) return true
  }

  // Fallback: tokenless rows can still be recreated during React rerenders.
  // Avoid pushing updateData when the row is structurally unchanged, because
  // any out-of-band grid update can invalidate AG Grid native undo/redo.
  return !isEqual(previous, next)
}

const syncContextObject = (target: unknown, next: unknown) => {
  if (!isRecord(target) || !isRecord(next)) return false

  for (const key of Object.keys(target)) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      delete target[key]
    }
  }

  for (const [key, value] of Object.entries(next)) {
    target[key] = value
  }

  return true
}

const normalizeContextRefreshSignals = (
  signals: ContextRefreshSignalMap | undefined,
): ContextRefreshSignalMap => signals ?? {}

const collectChangedContextSignals = (
  previousSignals: ContextRefreshSignalMap | undefined,
  nextSignals: ContextRefreshSignalMap | undefined,
) => {
  const prev = normalizeContextRefreshSignals(previousSignals)
  const next = normalizeContextRefreshSignals(nextSignals)
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)])

  return Array.from(keys)
    .filter((key) => !Object.is(prev[key], next[key]))
    .sort()
}

const isGridDebugEnabled = () =>
  typeof window !== 'undefined' && process.env.NODE_ENV !== 'production'

const createGridDebugStore = (): GridDebugStore => ({
  counters: {
    refreshCells: 0,
    redrawRows: 0,
    refreshClientSideRowModel: 0,
    undoEnded: 0,
    redoEnded: 0,
  },
  reset() {
    this.counters.refreshCells = 0
    this.counters.redrawRows = 0
    this.counters.refreshClientSideRowModel = 0
    this.counters.undoEnded = 0
    this.counters.redoEnded = 0
  },
})

const getGridDebugStore = (): GridDebugStore | null => {
  if (!isGridDebugEnabled()) return null

  const win = window as typeof window & {
    __SKOP_GRID_DEBUG__?: GridDebugStore
  }

  if (!win.__SKOP_GRID_DEBUG__) {
    win.__SKOP_GRID_DEBUG__ = createGridDebugStore()
  }

  return win.__SKOP_GRID_DEBUG__
}

const incrementGridDebugCounter = (counter: keyof GridDebugCounters) => {
  const store = getGridDebugStore()
  if (!store) return
  store.counters[counter] += 1
}

const mergeUniqueStrings = (
  left: string[] | undefined,
  right: string[] | undefined,
) => {
  const merged = [...(left ?? []), ...(right ?? [])]
  return merged.length > 0 ? Array.from(new Set(merged)) : undefined
}

const mergePlanRowIds = (left: string[], right: string[]) => {
  if (left.length === 0 || right.length === 0) return []
  return mergeUniqueStrings(left, right) ?? []
}

const mergePlanColumns = (
  left: string[] | undefined,
  right: string[] | undefined,
) => {
  if (left === undefined || right === undefined) return undefined
  return mergeUniqueStrings(left, right)
}

const mergeContextRefreshPlans = (
  left: GridContextRefreshPlan,
  right: GridContextRefreshPlan,
): GridContextRefreshPlan => ({
  rowIds: mergePlanRowIds(left.rowIds, right.rowIds),
  columns: mergePlanColumns(left.columns, right.columns),
  includeParents:
    left.includeParents === false && right.includeParents === false
      ? false
      : true,
  redrawRows:
    left.redrawRows === false && right.redrawRows === false ? false : true,
  force: left.force === false && right.force === false ? false : true,
})

const resolveContextVisualRefresh = ({
  contextChanged,
  hasCustomContextRefresh,
  contextRefreshPlan,
  shouldSkipVisualRefresh,
}: {
  contextChanged: boolean
  hasCustomContextRefresh: boolean
  contextRefreshPlan: GridContextRefreshPlan | null | undefined
  shouldSkipVisualRefresh: boolean
}): ResolvedContextVisualRefresh => {
  if (!contextChanged) return { kind: 'none' }
  if (
    shouldSkipVisualRefresh ||
    (hasCustomContextRefresh && contextRefreshPlan === null)
  ) {
    return { kind: 'none' }
  }
  if (contextRefreshPlan) {
    return { kind: 'plan', plan: contextRefreshPlan }
  }
  return { kind: 'broad' }
}

const mergeResolvedContextVisualRefresh = (
  left: ResolvedContextVisualRefresh,
  right: ResolvedContextVisualRefresh,
): ResolvedContextVisualRefresh => {
  if (left.kind === 'broad' || right.kind === 'broad') {
    return { kind: 'broad' }
  }
  if (left.kind === 'none') return right
  if (right.kind === 'none') return left
  return {
    kind: 'plan',
    plan: mergeContextRefreshPlans(left.plan, right.plan),
  }
}

const mergePendingVisualContextRefresh = (
  current: PendingVisualContextRefresh | null,
  next: PendingVisualContextRefresh,
): PendingVisualContextRefresh => {
  if (!current) return next
  return {
    contextVisualRefresh: mergeResolvedContextVisualRefresh(
      current.contextVisualRefresh,
      next.contextVisualRefresh,
    ),
    rowClassChanged: current.rowClassChanged || next.rowClassChanged,
    quickFilterInvalidated:
      current.quickFilterInvalidated || next.quickFilterInvalidated,
  }
}

const LEGACY_LOCAL_EDIT_IGNORE_TTL_MS = 1200
const CELL_VALUE_CHANGED_DEDUP_MS = 150
const CELL_CHANGES_EXPIRY_MS = 5 * 60 * 1000
const CELL_CHANGES_MAX_SIZE = 500
const UNDO_REDO_SYNC_EVENTS = [
  'cellValueChanged',
  'cellEditingStopped',
  'undoEnded',
  'redoEnded',
  'pasteEnd',
  'fillEnd',
  'batchEditingStopped',
  'bulkEditingStopped',
] as const

const collectAffectedRowNodes = <Row,>(
  api: { getRowNode: (id: string) => IRowNode<Row> | undefined },
  rowIds: string[],
  includeParents = true,
) => {
  const nodes = new Set<IRowNode<Row>>()

  rowIds.forEach((rowId) => {
    let currentNode = api.getRowNode(rowId)

    while (currentNode) {
      nodes.add(currentNode)
      currentNode = includeParents
        ? (currentNode.parent ?? undefined)
        : undefined
    }
  })

  return Array.from(nodes)
}

export function GridTable<Row, Ctx>() {
  const { gridRef, api, gridIsReady } = useGridApi()
  const {
    agGridProps,
    rowDataScopeKey,
    gridContext,
    getContextMenuItems,
    onFilterChanged,
    onGridReady,
    isExternalFilterPresent,
    doesExternalFilterPass,
    hasExternalFilter,
    aggregationConfig,
    contextRefreshConfig,
    isInteractionBlocked = false,
  } = useGridTable<Row, Ctx>()

  const {
    context: _context,
    getContextMenuItems: _getContextMenuItems,
    onFilterChanged: _onFilterChanged,
    onGridReady: _onGridReady,
    isExternalFilterPresent: _isExternalFilterPresent,
    doesExternalFilterPass: _doesExternalFilterPass,
    rowData: controlledRowData,
    onCellValueChanged: onCellValueChangedProp,
    getDataPath,
    getRowClass: gridRowClass,
    initialState: initialGridState,
    ...restProps
  } = agGridProps
  const hasAppliedGridOptionsRef = useRef(false)
  const syncedContextRef = useRef(gridContext)
  const syncedGetRowClassRef = useRef(gridRowClass)
  const bootContextRef = useRef(gridContext)
  const bootGetRowClassRef = useRef(gridRowClass)
  const pendingVisualContextRefreshRef =
    useRef<PendingVisualContextRefresh | null>(null)
  const refreshCells = useCallback(
    (params?: Parameters<NonNullable<typeof api>['refreshCells']>[0]) => {
      if (!api) return
      incrementGridDebugCounter('refreshCells')
      api.refreshCells(params)
    },
    [api],
  )
  const redrawRows = useCallback(
    (params?: Parameters<NonNullable<typeof api>['redrawRows']>[0]) => {
      if (!api) return
      incrementGridDebugCounter('redrawRows')
      api.redrawRows(params)
    },
    [api],
  )
  const refreshClientSideRowModel = useCallback(
    (
      step: Parameters<NonNullable<typeof api>['refreshClientSideRowModel']>[0],
    ) => {
      if (!api) return
      incrementGridDebugCounter('refreshClientSideRowModel')
      api.refreshClientSideRowModel(step)
    },
    [api],
  )
  const hasActiveEditingSession = useCallback(() => {
    if (!api || !gridIsReady) return false
    return (api.getEditingCells?.()?.length ?? 0) > 0
  }, [api, gridIsReady])
  const invalidateQuickFilter = useCallback(() => {
    if (!api) return
    api.resetQuickFilter?.()
    if (api.isQuickFilterPresent?.()) {
      api.onFilterChanged?.()
    }
  }, [api])
  const applyContextRefreshPlan = useCallback(
    (
      plan: GridContextRefreshPlan,
      options?: {
        redrawAllRows?: boolean
      },
    ) => {
      if (!api) return

      const rowNodes = collectAffectedRowNodes(
        api,
        plan.rowIds,
        plan.includeParents !== false,
      )
      const refreshParams = {
        force: plan.force ?? true,
        rowNodes: rowNodes.length > 0 ? rowNodes : undefined,
        columns: plan.columns,
      }

      if (rowNodes.length > 0 || !plan.rowIds.length) {
        refreshCells(refreshParams)
        if (options?.redrawAllRows) {
          redrawRows()
        } else if (plan.redrawRows !== false) {
          redrawRows(rowNodes.length > 0 ? { rowNodes } : undefined)
        }
        return
      }

      refreshCells({
        force: plan.force ?? true,
        columns: plan.columns,
      })
      if (options?.redrawAllRows) {
        redrawRows()
      } else if (plan.redrawRows !== false) {
        redrawRows()
      }
    },
    [api, redrawRows, refreshCells],
  )
  const applyResolvedContextVisualRefresh = useCallback(
    (
      visualRefresh: ResolvedContextVisualRefresh,
      options?: {
        redrawAllRows?: boolean
      },
    ) => {
      if (visualRefresh.kind === 'none') return
      if (visualRefresh.kind === 'broad') {
        refreshCells({ force: true })
        redrawRows()
        return
      }

      applyContextRefreshPlan(visualRefresh.plan, options)
    },
    [applyContextRefreshPlan, redrawRows, refreshCells],
  )

  useEffect(() => {
    if (!gridIsReady || !api) return

    const isFirstSync = !hasAppliedGridOptionsRef.current
    const previousContext = syncedContextRef.current
    const contextChanged = previousContext !== gridContext
    const rowClassChanged = syncedGetRowClassRef.current !== gridRowClass
    const getContextRefreshSignals = contextRefreshConfig?.getSignals
    const getContextRefreshPlan = contextRefreshConfig?.getRefreshPlan
    const hasCustomContextRefresh = typeof getContextRefreshPlan === 'function'
    const changedSignals =
      contextChanged && typeof getContextRefreshSignals === 'function'
        ? collectChangedContextSignals(
            getContextRefreshSignals(previousContext),
            getContextRefreshSignals(gridContext),
          )
        : undefined
    const shouldSkipVisualRefresh =
      contextChanged &&
      typeof getContextRefreshSignals === 'function' &&
      changedSignals?.length === 0
    const shouldDeferVisualContextRefresh =
      !isFirstSync &&
      hasActiveEditingSession() &&
      (contextChanged || rowClassChanged)
    const contextRefreshPlan =
      contextChanged && hasCustomContextRefresh && !shouldSkipVisualRefresh
        ? getContextRefreshPlan({
            previousContext,
            nextContext: gridContext,
            changedSignals: changedSignals ?? [],
          })
        : undefined
    const resolvedContextVisualRefresh = isFirstSync
      ? ({ kind: 'broad' } as const)
      : resolveContextVisualRefresh({
          contextChanged,
          hasCustomContextRefresh,
          contextRefreshPlan,
          shouldSkipVisualRefresh,
        })

    if (isFirstSync || contextChanged) {
      const didMutateExistingContext =
        !isFirstSync && syncContextObject(bootContextRef.current, gridContext)

      if (isFirstSync || !didMutateExistingContext) {
        bootContextRef.current = gridContext
        api.setGridOption('context', bootContextRef.current)
      }

      if (!isFirstSync) {
        if (shouldDeferVisualContextRefresh) {
          pendingVisualContextRefreshRef.current =
            mergePendingVisualContextRefresh(
              pendingVisualContextRefreshRef.current,
              {
                contextVisualRefresh: { kind: 'none' },
                rowClassChanged: false,
                quickFilterInvalidated: true,
              },
            )
        } else {
          invalidateQuickFilter()
        }
      }

      if (shouldDeferVisualContextRefresh) {
        pendingVisualContextRefreshRef.current =
          mergePendingVisualContextRefresh(
            pendingVisualContextRefreshRef.current,
            {
              contextVisualRefresh: resolvedContextVisualRefresh,
              rowClassChanged,
              quickFilterInvalidated: false,
            },
          )
      } else {
        applyResolvedContextVisualRefresh(resolvedContextVisualRefresh)
      }
    }

    if (isFirstSync || rowClassChanged) {
      if (shouldDeferVisualContextRefresh) {
        if (!contextChanged) {
          pendingVisualContextRefreshRef.current =
            mergePendingVisualContextRefresh(
              pendingVisualContextRefreshRef.current,
              {
                contextVisualRefresh: { kind: 'none' },
                rowClassChanged: true,
                quickFilterInvalidated: false,
              },
            )
        }
      } else {
        api.setGridOption('getRowClass', gridRowClass)
        if (!(isFirstSync || contextChanged)) {
          redrawRows()
        }
      }
    }

    syncedContextRef.current = gridContext
    syncedGetRowClassRef.current = gridRowClass
    hasAppliedGridOptionsRef.current = true
  }, [
    api,
    contextRefreshConfig,
    gridContext,
    gridIsReady,
    gridRowClass,
    hasActiveEditingSession,
    applyResolvedContextVisualRefresh,
    redrawRows,
    refreshCells,
  ])

  useEffect(() => {
    if (gridIsReady) return
    if (!syncContextObject(bootContextRef.current, gridContext)) {
      bootContextRef.current = gridContext
    }
  }, [gridContext, gridIsReady])

  useEffect(() => {
    if (gridIsReady) return
    bootGetRowClassRef.current = gridRowClass
  }, [gridIsReady, gridRowClass])

  const getRowIdProp = agGridProps.getRowId
  const getRowIdFromRow = useCallback(
    (row: Row): string | null => {
      if (typeof getRowIdProp === 'function') {
        const id = getRowIdProp({ data: row } as GetRowIdParams<Row>)
        return coerceRowId(id)
      }
      if (isRecord(row)) return coerceRowId(row.id)
      return null
    },
    [getRowIdProp],
  )

  const emptyRowData = useMemo<Row[]>(() => [], [])
  const nextBootRowData = Array.isArray(controlledRowData)
    ? (controlledRowData as Row[])
    : emptyRowData

  const [frozenBootRowData, setFrozenBootRowData] =
    useState<Row[]>(nextBootRowData)

  useEffect(() => {
    if (gridIsReady) return

    setFrozenBootRowData((previous) =>
      previous === nextBootRowData ? previous : nextBootRowData,
    )
  }, [gridIsReady, nextBootRowData])

  const bootRowData = gridIsReady ? frozenBootRowData : nextBootRowData

  const previousByIdRef = useRef<Map<string, Row>>(new Map())
  const localEditsRef = useRef(new Map<string, LocalRowTransition<Row>>())
  const legacyLocalEditsRef = useRef(new Map<string, number>())
  const latestControlledByIdRef = useRef(new Map<string, Row>())
  const rowDataOwnerRef = useRef({ api, rowDataScopeKey })
  const rowDataEpochRef = useRef(0)
  const recentCellChangesRef = useRef(
    new Map<string, { at: number; oldValue: unknown; newValue: unknown }>(),
  )
  const deferredControlledRowDataRef = useRef<Row[] | null>(null)
  const deferredControlledRowDataTimerRef = useRef<number | null>(null)
  const flushDeferredControlledRowDataRef = useRef<() => void>(() => {})

  useEffect(() => {
    const previousOwner = rowDataOwnerRef.current
    if (
      previousOwner.api === api &&
      previousOwner.rowDataScopeKey === rowDataScopeKey
    )
      return

    rowDataOwnerRef.current = { api, rowDataScopeKey }
    rowDataEpochRef.current += 1
    localEditsRef.current.clear()
    legacyLocalEditsRef.current.clear()
    recentCellChangesRef.current.clear()
    deferredControlledRowDataRef.current = null
    if (deferredControlledRowDataTimerRef.current !== null) {
      window.clearTimeout(deferredControlledRowDataTimerRef.current)
      deferredControlledRowDataTimerRef.current = null
    }
    pendingVisualContextRefreshRef.current = null
    if (previousOwner.api === api) api?.stopEditing(true)
  }, [api, rowDataScopeKey])

  useEffect(() => {
    if (gridIsReady) return
    if (!Array.isArray(controlledRowData)) return
    const nextById = new Map<string, Row>()
    for (const row of controlledRowData as Row[]) {
      const id = getRowIdFromRow(row)
      if (!id) continue
      // AG Grid setters mutate row objects. Keep a detached baseline so a
      // later rerender cannot turn the pre-edit snapshot into an acknowledgement.
      nextById.set(id, cloneDeep(row))
    }
    previousByIdRef.current = nextById
    latestControlledByIdRef.current = nextById
  }, [controlledRowData, getRowIdFromRow, gridIsReady])

  const getPathKey = useCallback(
    (row: Row): string | null => {
      if (typeof getDataPath !== 'function') return null
      try {
        return JSON.stringify(getDataPath(row))
      } catch {
        return null
      }
    },
    [getDataPath],
  )

  const getActiveEditingRowIds = useCallback(() => {
    if (!api || !gridIsReady) return new Set<string>()

    const rowIds = new Set<string>()
    const editingCells = api.getEditingCells?.() ?? []

    editingCells.forEach((cell) => {
      const rowIndex =
        typeof cell.rowIndex === 'number' ? cell.rowIndex : undefined
      if (rowIndex === undefined) return

      const rowNode = api.getDisplayedRowAtIndex?.(rowIndex)
      const rowId =
        (rowNode?.data && getRowIdFromRow(rowNode.data as Row)) ||
        coerceRowId(rowNode?.id)

      if (rowId) rowIds.add(rowId)
    })

    return rowIds
  }, [api, getRowIdFromRow, gridIsReady])

  const flushPendingVisualContextRefresh = useCallback(() => {
    if (!api || !gridIsReady) return
    const pendingVisualContextRefresh = pendingVisualContextRefreshRef.current
    if (!pendingVisualContextRefresh) return
    if (hasActiveEditingSession()) return

    if (pendingVisualContextRefresh.rowClassChanged) {
      api.setGridOption('getRowClass', syncedGetRowClassRef.current)
    }
    if (pendingVisualContextRefresh.quickFilterInvalidated) {
      invalidateQuickFilter()
    }

    if (
      pendingVisualContextRefresh.rowClassChanged &&
      pendingVisualContextRefresh.contextVisualRefresh.kind === 'none'
    ) {
      redrawRows()
    } else {
      applyResolvedContextVisualRefresh(
        pendingVisualContextRefresh.contextVisualRefresh,
        {
          redrawAllRows: pendingVisualContextRefresh.rowClassChanged,
        },
      )
    }

    pendingVisualContextRefreshRef.current = null
  }, [
    api,
    gridIsReady,
    hasActiveEditingSession,
    invalidateQuickFilter,
    redrawRows,
    applyResolvedContextVisualRefresh,
  ])

  const applyRowDataDiff = useCallback(
    (nextRows: Row[]) => {
      if (!api || !gridIsReady) return

      const activeEditingRowIds = getActiveEditingRowIds()
      const previousById = previousByIdRef.current
      const nextById = new Map<string, Row>()
      const controlledById = new Map<string, Row>()

      const add: Row[] = []
      const remove: Row[] = []
      let hasSingleRowUpdates = false
      let refreshAfterRowDataDiffStep: 'group' | 'aggregate' | null = null
      let hasDeferredProtectedRows = false
      let nextLegacyFlushDelayMs: number | null = null
      const now = Date.now()
      const hasLegacyGrace = (id: string) => {
        const editedAt = legacyLocalEditsRef.current.get(id)
        if (editedAt === undefined) return false
        const remaining = LEGACY_LOCAL_EDIT_IGNORE_TTL_MS - (now - editedAt)
        if (remaining <= 0) {
          legacyLocalEditsRef.current.delete(id)
          return false
        }
        nextLegacyFlushDelayMs = Math.min(
          nextLegacyFlushDelayMs ?? remaining,
          remaining,
        )
        return true
      }

      const updateNode = (node: IRowNode<Row>, row: Row) => {
        const livePrevious = node.data
        if (!livePrevious || !shouldUpdateRow(livePrevious, row)) return
        node.updateData(cloneDeep(row))
        hasSingleRowUpdates = true
        if (
          shouldRefreshAggregationAfterRowDataDiff(
            { previousRow: livePrevious, nextRow: row },
            aggregationConfig?.refreshAfterRowDataDiff,
          )
        ) {
          refreshAfterRowDataDiffStep =
            aggregationConfig?.refreshAfterRowDataDiff?.step ?? 'group'
        }
      }

      for (const row of nextRows) {
        const id = getRowIdFromRow(row)
        if (!id) continue
        const previous = previousById.get(id)
        const previousControlled = latestControlledByIdRef.current.get(id)
        const incomingSnapshot =
          previousControlled && !shouldUpdateRow(previousControlled, row)
            ? previousControlled
            : cloneDeep(row)
        controlledById.set(id, incomingSnapshot)
        const node = api.getRowNode(id)
        const liveRow = node?.data as Row | undefined
        const localTransition = localEditsRef.current.get(id)

        if (activeEditingRowIds.has(id)) {
          nextById.set(id, previous ?? cloneDeep(liveRow ?? row))
          hasDeferredProtectedRows =
            shouldUpdateRow(liveRow ?? previous ?? row, row) ||
            hasDeferredProtectedRows
          continue
        }

        if (hasLegacyGrace(id)) {
          nextById.set(id, previous ?? cloneDeep(liveRow ?? row))
          hasDeferredProtectedRows = true
          continue
        }

        if (localTransition && liveRow) {
          rememberControlledRow(localTransition, incomingSnapshot)
          if (shouldDeferLocalRow(localTransition, row, liveRow)) {
            nextById.set(id, previous ?? cloneDeep(liveRow))
            hasDeferredProtectedRows = true
            continue
          }
          if (localTransition.pending.size === 0) {
            // An acknowledgement needs no grid write. A different canonical
            // snapshot is admitted once persistence has settled; old snapshots
            // alone never become authoritative merely because time passed.
            localEditsRef.current.delete(id)
          }
        }

        nextById.set(id, incomingSnapshot)

        if (!previous) {
          if (node) updateNode(node, row)
          else add.push(row)
          continue
        }

        // updateData cannot reposition a tree node. Structural changes keep
        // their existing transaction path, outside native cell undo history.
        const prevPath = getPathKey(previous)
        const nextPath = getPathKey(row)
        if (prevPath !== null && nextPath !== null && prevPath !== nextPath) {
          remove.push(liveRow ?? previous)
          add.push(row)
          continue
        }

        if (node) updateNode(node, row)
      }

      for (const [id, previous] of previousById.entries()) {
        if (nextById.has(id)) continue

        // A feature may already have replaced a placeholder or removed a row.
        // Do not replay the transaction when its controlled snapshot catches up.
        const node = api.getRowNode(id)
        if (!node) {
          localEditsRef.current.delete(id)
          legacyLocalEditsRef.current.delete(id)
          continue
        }

        if (
          activeEditingRowIds.has(id) ||
          (localEditsRef.current.get(id)?.pending.size ?? 0) > 0 ||
          hasLegacyGrace(id)
        ) {
          nextById.set(id, previous)
          hasDeferredProtectedRows = true
          continue
        }

        localEditsRef.current.delete(id)
        remove.push((node.data as Row | undefined) ?? previous)
      }

      // Only structural changes require a transaction. Equal acknowledgements
      // and stale rerenders must not repaint unrelated rows or clear undo.
      const tx: { add?: Row[]; remove?: Row[] } = {}
      if (remove.length > 0) tx.remove = remove
      if (add.length > 0) tx.add = add.map((row) => cloneDeep(row))
      if (tx.add || tx.remove) {
        clearRemovedVirtualizedCellFocus(
          api,
          remove.filter((row) => {
            const id = getRowIdFromRow(row)
            return id !== null && !nextById.has(id)
          }),
          getRowIdFromRow,
        )
        api.applyTransaction(tx)
      } else if (hasSingleRowUpdates && refreshAfterRowDataDiffStep) {
        refreshClientSideRowModel(refreshAfterRowDataDiffStep)
      }

      previousByIdRef.current = nextById
      latestControlledByIdRef.current = controlledById
      return { hasDeferredProtectedRows, nextLegacyFlushDelayMs }
    },
    [
      aggregationConfig,
      api,
      getActiveEditingRowIds,
      getPathKey,
      getRowIdFromRow,
      gridIsReady,
      refreshClientSideRowModel,
    ],
  )

  const scheduleLegacyControlledReplay = useCallback((delay: number | null) => {
    if (deferredControlledRowDataTimerRef.current !== null) {
      window.clearTimeout(deferredControlledRowDataTimerRef.current)
      deferredControlledRowDataTimerRef.current = null
    }
    if (delay === null) return
    deferredControlledRowDataTimerRef.current = window.setTimeout(
      () => {
        deferredControlledRowDataTimerRef.current = null
        flushDeferredControlledRowDataRef.current()
      },
      Math.max(delay, 0),
    )
  }, [])

  const flushDeferredControlledRowData = useCallback(() => {
    const deferredRows = deferredControlledRowDataRef.current
    if (!deferredRows || hasActiveEditingSession()) return
    const diffResult = applyRowDataDiff(deferredRows)
    if (!diffResult?.hasDeferredProtectedRows) {
      deferredControlledRowDataRef.current = null
    }
    scheduleLegacyControlledReplay(diffResult?.nextLegacyFlushDelayMs ?? null)
  }, [
    applyRowDataDiff,
    hasActiveEditingSession,
    scheduleLegacyControlledReplay,
  ])

  useEffect(() => {
    flushDeferredControlledRowDataRef.current = flushDeferredControlledRowData
  }, [flushDeferredControlledRowData])

  useEffect(() => {
    if (!gridIsReady || !api) return
    const nextRows = Array.isArray(controlledRowData)
      ? (controlledRowData as Row[])
      : emptyRowData
    const diffResult = applyRowDataDiff(nextRows)
    deferredControlledRowDataRef.current = diffResult?.hasDeferredProtectedRows
      ? Array.from(latestControlledByIdRef.current.values())
      : null
    scheduleLegacyControlledReplay(diffResult?.nextLegacyFlushDelayMs ?? null)
  }, [
    api,
    applyRowDataDiff,
    controlledRowData,
    emptyRowData,
    gridIsReady,
    rowDataScopeKey,
    scheduleLegacyControlledReplay,
  ])

  useEffect(() => {
    if (!gridIsReady || !api) return

    const eventHandlers = new Map<
      (typeof UNDO_REDO_SYNC_EVENTS)[number],
      () => void
    >()
    for (const eventName of UNDO_REDO_SYNC_EVENTS) {
      const handler = () => {
        if (eventName === 'undoEnded' || eventName === 'redoEnded') {
          incrementGridDebugCounter(eventName)
        }
        // AG Grid dispatches API listeners before its onCellValueChanged prop.
        // Let that callback register the just-committed transition first.
        if (eventName !== 'cellValueChanged') flushDeferredControlledRowData()
        flushPendingVisualContextRefresh()
      }
      eventHandlers.set(eventName, handler)
      api.addEventListener?.(eventName, handler)
    }

    return () => {
      try {
        if (api.isDestroyed?.()) return
        for (const [eventName, handler] of eventHandlers.entries()) {
          api.removeEventListener?.(eventName, handler)
        }
      } catch {
        // grid may already be torn down during fast navigation
      }
    }
  }, [
    api,
    flushDeferredControlledRowData,
    flushPendingVisualContextRefresh,
    gridIsReady,
  ])

  useEffect(() => {
    return () => {
      rowDataEpochRef.current += 1
      if (deferredControlledRowDataTimerRef.current !== null) {
        window.clearTimeout(deferredControlledRowDataTimerRef.current)
      }
    }
  }, [])

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Row>) => {
      const row = event.data
      if (row) {
        const id = getRowIdFromRow(row)
        if (id) {
          const now = Date.now()
          const field =
            event.colDef.colId ??
            event.column?.getColId?.() ??
            event.colDef.field

          if (typeof field === 'string') {
            const dedupKey = `${id}:${field}`
            const lastChange = recentCellChangesRef.current.get(dedupKey)
            if (
              lastChange &&
              now - lastChange.at <= CELL_VALUE_CHANGED_DEDUP_MS &&
              isEqual(lastChange.oldValue, event.oldValue) &&
              isEqual(lastChange.newValue, event.newValue)
            ) {
              return
            }
            recentCellChangesRef.current.set(dedupKey, {
              at: now,
              oldValue: cloneDeep(event.oldValue),
              newValue: cloneDeep(event.newValue),
            })

            if (recentCellChangesRef.current.size > CELL_CHANGES_MAX_SIZE) {
              for (const [
                key,
                change,
              ] of recentCellChangesRef.current.entries()) {
                if (now - change.at > CELL_CHANGES_EXPIRY_MS) {
                  recentCellChangesRef.current.delete(key)
                }
              }
            }
          }
        }
      }

      const result: unknown = onCellValueChangedProp?.(event)
      const eventRowIds = event.node?.group
        ? (event.node.allLeafChildren ?? []).flatMap((node) => {
            const id = node.data && getRowIdFromRow(node.data)
            return id ? [id] : []
          })
        : row && getRowIdFromRow(row)
          ? [getRowIdFromRow(row)!]
          : []
      const operations = isCellEditOperation(result)
        ? [result]
        : Array.isArray(result) && result.every(isCellEditOperation)
          ? result
          : isPersistencePromise(result)
            ? [{ rowIds: eventRowIds, settled: result }]
            : []
      const epoch = rowDataEpochRef.current

      if (operations.length === 0) {
        // A void callback exposes no outcome. Preserve its existing refetch
        // recovery instead of assigning it an indefinite save acknowledgement.
        for (const id of eventRowIds) {
          const tracked = localEditsRef.current.get(id)
          const liveRow = api?.getRowNode(id)?.data
          if (
            tracked &&
            (tracked.pending.size > 0 || isEqual(tracked.submitted, liveRow))
          )
            continue
          localEditsRef.current.delete(id)
          legacyLocalEditsRef.current.set(id, Date.now())
        }
      }

      for (const operation of operations) {
        const rowIds = [...new Set(operation.rowIds)]
        const token = Symbol()
        for (const id of rowIds) {
          const liveRow =
            (api?.getRowNode(id)?.data as Row | undefined) ??
            (row && getRowIdFromRow(row) === id ? row : undefined)
          if (!liveRow) continue
          legacyLocalEditsRef.current.delete(id)
          let transition = localEditsRef.current.get(id)
          if (!transition) {
            transition = createLocalRowTransition(
              previousByIdRef.current.get(id),
              liveRow,
            )
            localEditsRef.current.set(id, transition)
          }
          recordLocalRowOperation(transition, token, liveRow)
        }

        const settle = (rejected: boolean) => {
          if (epoch !== rowDataEpochRef.current || api?.isDestroyed?.()) return
          const remove: Row[] = []
          for (const id of rowIds) {
            const transition = localEditsRef.current.get(id)
            if (!transition?.pending.has(token)) continue
            const liveRow = api?.getRowNode(id)?.data as Row | undefined
            if (!liveRow) {
              localEditsRef.current.delete(id)
              continue
            }
            const controlled = latestControlledByIdRef.current.get(id)
            settleLocalRowOperation(
              transition,
              token,
              liveRow,
              controlled,
              rejected,
            )
            if (transition.pending.size > 0) continue
            if (rejected && transition.isUnconfirmedAddition && !controlled) {
              remove.push(liveRow)
              localEditsRef.current.delete(id)
              previousByIdRef.current.delete(id)
            } else if (controlled && isEqual(controlled, liveRow)) {
              localEditsRef.current.delete(id)
            }
          }
          if (api && remove.length > 0) {
            clearRemovedVirtualizedCellFocus(api, remove, getRowIdFromRow)
            api.applyTransaction({ remove })
          }
          flushDeferredControlledRowDataRef.current()
        }
        void Promise.resolve(operation.settled).then(
          () => settle(false),
          () => settle(true),
        )
      }
      refreshAggregationAfterCellEdit(
        event,
        aggregationConfig?.refreshAfterCellEdit,
      )
      flushDeferredControlledRowDataRef.current()
    },
    [aggregationConfig, api, getRowIdFromRow, onCellValueChangedProp],
  )

  const handleBlockedKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isInteractionBlocked) return
      if (event.key === 'Tab') {
        event.stopPropagation()
        return
      }
      event.preventDefault()
      event.stopPropagation()
    },
    [isInteractionBlocked],
  )

  useEffect(() => {
    if (!isInteractionBlocked || !api) return
    api.stopEditing()
    api.hidePopupMenu()
    api.hideColumnChooser()
  }, [api, isInteractionBlocked])

  return (
    <div
      className="relative h-full w-full"
      data-grid-interaction-blocked={isInteractionBlocked ? 'true' : 'false'}
      aria-disabled={isInteractionBlocked || undefined}
      onKeyDownCapture={isInteractionBlocked ? handleBlockedKeyDown : undefined}
    >
      <AGGrid
        ref={gridRef}
        {...restProps}
        initialState={initialGridState}
        rowData={bootRowData}
        context={gridIsReady ? bootContextRef.current : gridContext}
        getContextMenuItems={getContextMenuItems}
        onFilterChanged={onFilterChanged}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
        getDataPath={getDataPath}
        getRowClass={gridIsReady ? bootGetRowClassRef.current : gridRowClass}
        isExternalFilterPresent={
          hasExternalFilter ? isExternalFilterPresent : _isExternalFilterPresent
        }
        doesExternalFilterPass={
          hasExternalFilter ? doesExternalFilterPass : _doesExternalFilterPass
        }
      />
      {isInteractionBlocked ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 z-10 cursor-not-allowed bg-transparent"
          data-testid="grid-interaction-blocked-overlay"
        />
      ) : null}
    </div>
  )
}
